import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Button, Chip, IconButton,  Typography, CircularProgress, Stack, Avatar
} from '@mui/material'
import {
  Add as AddIcon,
  ChatBubbleOutline as CommentIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailMuiIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  Flag as FlagIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  TrendingUp as DealIcon,
  AttachMoney as AmountIcon,
  CalendarToday as CalIcon,
  EmojiEvents as WonIcon,
  Cancel as LostIcon,
  Work as WorkIcon,
  Link as LinkIcon,
} from '@mui/icons-material'
import type { Deal } from '../lib/api/dealsApi'
import { DEAL_STATUS_LABELS, DEAL_PRIORITY_LABELS } from '../lib/api/dealsApi'
import {
  CallIcon,
  DateIcon,
  EmailIcon,
  IndustryIcon,
  LocationIcon,
  WebsiteIcon
} from '../components/shared/CustomIcons'
import { toast } from 'react-toastify'
import {
  customersApi,
  UpdateCustomerPayload,
  CUSTOMER_SOURCES,
  Contact,
  Customer,
} from '../lib/api/customersApi'
import { maskPhoneNumber } from '../utils/phoneUtils'
import { leadsApi, SOURCE_LABELS, STATUS_LABELS, type LeadStatus } from '../lib/api/leadsApi'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { observer } from 'mobx-react-lite'
import { UniversalActivityTimeline } from '../components/shared/UniversalActivityTimeline'
import { EntityDetailHeader } from '../components/shared/EntityDetailHeader'
import { EntityDetailLayout } from '../components/shared/EntityDetailLayout'
import { EntityDetailTabs } from '../components/shared/EntityDetailTabs'
import { InfoRow } from '../components/shared/DetailViewWrappers'
import { SideCard } from '../components/shared/SideCard'
import { AddressAndTaxInfoCard } from '../components/shared/AddressAndTaxInfoCard'
import { DescriptionSection } from '../components/shared/DescriptionSection'
import LeadDrawer from '../components/drawers/LeadDrawer'
import { DealDrawer } from '../components/drawers/DealDrawer'
import { ContactDrawer } from '../components/drawers/ContactDrawer'
import { dealsApi } from '../lib/api/dealsApi'
import { usersAdminApi } from '../lib/api/usersAdminApi'
import { tagsApi, Tag } from '../lib/api/leadsApi'
import { TagModal } from '../components/shared/TagModal'
import AttachmentsSection from '../components/shared/AttachmentsSection'
import { CommentItem, CommentInput, CommentsEmptyState } from '../components/shared/NotesSection'
import { customerNotesApi } from '../lib/api/customerNotesApi'
import { FormInput } from '../components/ui'
import { usePermission } from '../lib/hooks/usePermission'

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map((n) => n[0]?.toUpperCase() ?? '').join('')
}



// ── Deals Tab ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  OPEN: { bg: '#eff6ff', color: '#2563eb' },
  WON:  { bg: '#f0fdf4', color: '#16a34a' },
  LOST: { bg: '#fef2f2', color: '#dc2626' },
}
const PRIORITY_COLORS: Record<string, { bg: string; color: string }> = {
  LOW:    { bg: '#f0fdf4', color: '#16a34a' },
  MEDIUM: { bg: '#fefce8', color: '#ca8a04' },
  HIGH:   { bg: '#fff7ed', color: '#ea580c' },
  URGENT: { bg: '#fef2f2', color: '#dc2626' },
}
function fmtAmt(amount: number | null, currency: string) {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(amount)
}
function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function DealsTab({ customerId, onAddDeal, onNavigate }: {
  customerId: string
  onAddDeal: () => void
  onNavigate: (id: string) => void
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['customer-deals', customerId],
    queryFn:  () => dealsApi.getAll({ customerId, pageSize: 50 }),
    enabled:  !!customerId,
  })
  const deals = data?.items ?? []

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={24} />
    </Box>
  )

  return (
    <Box>
      {deals.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: '12px', border: '2px dashed #e5e7eb' }}>
          <DealIcon sx={{ fontSize: 36, color: '#d1d5db', mb: 1 }} />
          <Typography fontSize="14px" fontWeight={600} color="text.secondary">No deals yet</Typography>
          <Typography fontSize="12px" color="text.disabled" mt={0.5}>Create a deal to track opportunities</Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddDeal}
            sx={{ mt: 2, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
            New Deal
          </Button>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {deals.map((deal: Deal) => {
            const sc = STATUS_COLORS[deal.status] ?? STATUS_COLORS.OPEN
            const pc = PRIORITY_COLORS[deal.priority] ?? PRIORITY_COLORS.MEDIUM
            return (
              <Box key={deal.id} onClick={() => onNavigate(deal.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, p: 1.75,
                  border: '1px solid #e5e7eb', borderRadius: '10px', bgcolor: '#fff',
                  cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { borderColor: themeColors.primary, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                }}>
                {/* Icon */}
                <Box sx={{ width: 38, height: 38, borderRadius: '9px', bgcolor: '#ede9fe',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DealIcon sx={{ fontSize: 20, color: '#6d28d9' }} />
                </Box>
                {/* Main info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                    <Typography fontSize="13px" fontWeight={700} noWrap>{deal.title}</Typography>
                    <Chip label={DEAL_STATUS_LABELS[deal.status] ?? deal.status} size="small"
                      sx={{ height: 17, fontSize: '10px', fontWeight: 700, borderRadius: '4px',
                        bgcolor: sc.bg, color: sc.color, '& .MuiChip-label': { px: '5px' } }} />
                    <Chip label={deal.stage} size="small"
                      sx={{ height: 17, fontSize: '10px', fontWeight: 600, borderRadius: '4px',
                        bgcolor: '#ede9fe', color: '#6d28d9', '& .MuiChip-label': { px: '5px' } }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <AmountIcon sx={{ fontSize: 12, color: '#9ca3af' }} />
                      <Typography fontSize="12px" color="text.secondary" fontWeight={600}>
                        {fmtAmt(deal.amount, deal.currency)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <CalIcon sx={{ fontSize: 12, color: '#9ca3af' }} />
                      <Typography fontSize="12px" color="text.secondary">
                        {fmtDate(deal.expectedCloseDate)}
                      </Typography>
                    </Box>
                    {deal.owner && (
                      <Typography fontSize="12px" color="text.disabled" noWrap>
                        {deal.owner.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* Priority chip */}
                <Chip label={DEAL_PRIORITY_LABELS[deal.priority] ?? deal.priority} size="small"
                  sx={{ height: 18, fontSize: '10px', fontWeight: 700, borderRadius: '4px',
                    bgcolor: pc.bg, color: pc.color, '& .MuiChip-label': { px: '6px' }, flexShrink: 0 }} />
                {/* Won/Lost badges */}
                {(deal.isWon || deal.status === 'WON') && <WonIcon sx={{ fontSize: 18, color: '#16a34a', flexShrink: 0 }} />}
                {deal.isLost && <LostIcon sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />}
                <IconButton size="small" onClick={e => { e.stopPropagation(); onNavigate(deal.id) }}
                  sx={{ bgcolor: themeColors.primary, color: '#fff', '&:hover': { bgcolor: themeColors.primaryDark },
                    width: 26, height: 26, flexShrink: 0 }}>
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

// ── Contacts Tab ─────────────────────────────────────────────────────────────

// ── Leads Tab ────────────────────────────────────────────────────────────────

function LeadsTab({ leads, isLoading, onNavigate }: {
  leads: any[]
  isLoading: boolean
  onNavigate: (id: string) => void
}) {
  const [search, setSearch] = useState('')
  
  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>

  const filtered = leads.filter(l => 
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.email?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormInput
          placeholder="Search name, company, email"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' } }}
          InputProps={{
            startAdornment: (
              <Box sx={{ color: 'text.disabled', mr: 1, display: 'flex' }}>
                <SearchIcon sx={{ fontSize: 20 }} />
              </Box>
            )
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          sx={{ borderRadius: '8px', textTransform: 'none', color: '#374151', borderColor: '#E5E7EB', bgcolor: '#fff' }}
        >
          Filters
        </Button>
      </Box>

      {filtered.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: '12px', border: '2px dashed #e5e7eb' }}>
          <Typography fontSize="14px" color="text.secondary">No leads found</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filtered.map(l => (
            <Box key={l.id} sx={{
              p: 2, border: '1px solid #e5e7eb', borderRadius: '12px', bgcolor: '#fff',
              display: 'flex', flexDirection: 'column', gap: 1.5,
              cursor: 'pointer', transition: 'all 0.15s',
              '&:hover': { borderColor: themeColors.primary, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }
            }} onClick={() => onNavigate(l.id)}>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography fontSize="12px" color="text.secondary">Lead Name:</Typography>
                  <Typography fontSize="13px" fontWeight={700}>{l.name}</Typography>
                </Box>
                <Chip
                  label={STATUS_LABELS[l.status as LeadStatus] ?? l.status}
                  size="small"
                  sx={{
                    height: 22, fontSize: '11px', fontWeight: 700,
                    bgcolor: l.status === 'CONVERTED' ? '#e8ffe3' : l.status === 'QUALIFIED' ? '#fdf2f8' : '#f3f4f6',
                    color: l.status === 'CONVERTED' ? '#197C00' : l.status === 'QUALIFIED' ? '#db2777' : '#374151',
                    borderRadius: '6px'
                  }}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography fontSize="12px" color="text.secondary">Contact Number:</Typography>
                  <Typography fontSize="12.5px" fontWeight={500}>{maskPhoneNumber(l.phone)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Typography fontSize="12px" color="text.secondary">Email:</Typography>
                  <Typography fontSize="12.5px" fontWeight={500} noWrap>{l.email || '—'}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography fontSize="12px" color="text.secondary">Source:</Typography>
                  <Typography fontSize="12.5px" fontWeight={500}>{SOURCE_LABELS[l.source] || l.source}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Typography fontSize="12px" color="text.secondary">Owner:</Typography>
                  <Typography fontSize="12.5px" fontWeight={500}>{l.owner?.name || '—'}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}

function ContactsTab({ contacts, isLoading, onNavigate, onAdd, customer }: {
  contacts: Contact[]
  isLoading: boolean
  onNavigate: (id: string) => void
  onAdd: () => void
  customer: Customer
}) {
  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
  return (
    <Box>
      {contacts.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: '12px', border: '2px dashed #e5e7eb' }}>
          <Typography fontSize="14px" color="text.secondary">No contacts yet</Typography>
          <Typography fontSize="12px" color="text.disabled" mt={0.5}>Add a contact to get started</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {[...contacts].sort((a, b) => {
            const aPrimary = a.role === 'Primary Contact' || a.name === customer.name || a.isPrimary
            const bPrimary = b.role === 'Primary Contact' || b.name === customer.name || b.isPrimary
            return (aPrimary ? 0 : 1) - (bPrimary ? 0 : 1)
          }).map(c => {
            const initials = c.name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
            const isPrimary = c.role === 'Primary Contact' || c.name === customer.name || c.isPrimary
            const designation = isPrimary ? customer.designation : (c.role && c.role !== 'Contact' ? c.role : null)
            return (
              <Box key={c.id} sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 1.5,
                border: '1px solid #e5e7eb', borderRadius: '10px', bgcolor: '#fff',
                cursor: 'pointer', transition: 'all 0.15s',
                '&:hover': { borderColor: themeColors.primary, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }
              }} onClick={() => onNavigate(c.id)}>
                <Avatar sx={{ width: 38, height: 38, bgcolor: themeColors.primary, fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>
                  {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography fontSize="13px" fontWeight={700} noWrap>{c.name}</Typography>
                    {isPrimary && (
                      <Chip label="Primary" size="small" sx={{ height: 16, fontSize: '10px', fontWeight: 600, bgcolor: '#dbeafe', color: '#1d4ed8', '& .MuiChip-label': { px: 0.75 } }} />
                    )}
                  </Box>
                </Box>
                <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
                  {designation && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WorkIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                      <Typography fontSize="12px" color="text.secondary">{designation}</Typography>
                    </Box>
                  )}
                  {c.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                      <Typography fontSize="12px" color="text.secondary">{maskPhoneNumber(c.phone)}</Typography>
                    </Box>
                  )}
                  {c.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailMuiIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                      <Typography fontSize="12px" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>{c.email}</Typography>
                    </Box>
                  )}
                </Stack>
                <IconButton size="small" onClick={e => { e.stopPropagation(); onNavigate(c.id) }}
                  sx={{ bgcolor: themeColors.primary, color: '#fff', '&:hover': { bgcolor: themeColors.primaryDark }, width: 26, height: 26, flexShrink: 0 }}>
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const CustomerDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const userId = authStore.user?.userId ?? ''

  const { data: customer, isLoading: loadingCustomer } = useQuery({ queryKey: ['customer', id], queryFn: () => customersApi.getById(id!), enabled: !!id })
  const { data: activityLogs = [], isLoading: loadingActivity } = useQuery({ queryKey: ['customer-activity', id], queryFn: () => customersApi.getActivity(id!), enabled: !!id })

  // ── Deal stats (always fetched so the header stats are real) ─────────────────
  const { data: dealsData } = useQuery({
    queryKey: ['customer-deals', id],
    queryFn:  () => dealsApi.getAll({ customerId: id!, pageSize: 200 }),
    enabled:  !!id,
  })
  const { data: usersData } = useQuery({ queryKey: ['users-list'], queryFn: () => usersAdminApi.getAll({ pageSize: 1000 }), staleTime: 60_000 })
  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: ['tags', customer?.orgId],
    queryFn: () => tagsApi.getAll(customer?.orgId ? { orgId: customer.orgId } : undefined),
    staleTime: 60_000,
    enabled: !!customer,
  })

  const [activeTab, setActiveTab] = useState(0)
  const { data: contacts = [], isLoading: loadingContacts } = useQuery({ queryKey: ['customer-contacts', id], queryFn: () => customersApi.getContacts(id!), enabled: !!id && activeTab === 3 })

  const { data: leadsPaged, isLoading: loadingLeads } = useQuery({
    queryKey: ['customer-leads', id, customer?.companyName, customer?.orgId, customer?.lobId, customer?.warehouseId],
    queryFn: () => leadsApi.getAll({ 
      companyName: customer?.companyName || customer?.name, 
      orgIds: customer?.orgId ? [customer.orgId] : undefined,
      lobIds: customer?.lobId ? [customer.lobId] : undefined,
      warehouseIds: customer?.warehouseId ? [customer.warehouseId] : undefined,
      statuses: ['ALL'],
      pageSize: 100 
    }),
    enabled: !!customer && activeTab === 1
  })
  const leads = leadsPaged?.items || []
  const invalidateDeals = () => qc.invalidateQueries({ queryKey: ['customer-deals', id] })
  const [commentOpen, setCommentOpen] = useState(false)

  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false)
  const [dealDrawerOpen, setDealDrawerOpen] = useState(false)
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false)
  const [editingTaxInfo, setEditingTaxInfo] = useState(false)
  const [taxVals, setTaxVals] = useState({ address: '', gst: '', pincode: '' })
  const [taxErrors, setTaxErrors] = useState<{ gst?: string; pincode?: string }>({})

  const { hasAction } = usePermission()
  const canEdit = hasAction('/customers', 'update')
  const [tagModalOpen, setTagModalOpen] = useState(false)

  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ['customer-notes', id],
    queryFn: () => customerNotesApi.getAll(id!),
    enabled: !!id && activeTab === 4
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['customer', id] })
    qc.invalidateQueries({ queryKey: ['customer-activity', id] })
    qc.invalidateQueries({ queryKey: ['customer-contacts', id] })
    qc.invalidateQueries({ queryKey: ['customer-notes', id] })
  }

  const updateMut = useMutation({ mutationFn: (p: UpdateCustomerPayload) => customersApi.update(id!, p), onSuccess: () => { invalidate(); toast.success('Updated') }, onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Failed to update') })
  const createLeadMut = useMutation({
    mutationFn: (data: any) => leadsApi.create({
      name: `${data.firstName} ${data.lastName || ''}`.trim(),
      phone: data.contactNumber,
      email: data.email || null,
      source: data.source,
      companyName: data.companyName,
      address: data.address || undefined,
      gst: data.gst || undefined,
      pincode: data.pincode || undefined,
      orgId: data.orgId || '',
      lobId: data.lobId || '',
      warehouseId: data.warehouseId || '',
      nextAction: data.description || undefined,
      fileType: data.fileType || undefined,
    }),
    onSuccess: () => { invalidate(); setLeadDrawerOpen(false); toast.success('Lead created') },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Failed to create lead')
  })
  const addTagMut = useMutation({ mutationFn: (tid: string) => customersApi.addTag(id!, tid), onSuccess: () => { invalidate(); toast.success('Tag added') }, onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Failed to add tag') })
  const createDealMut = useMutation({
    mutationFn: (data: any) => dealsApi.create(data),
    onSuccess: (res) => {
      invalidate();
      invalidateDeals();
      setDealDrawerOpen(false);
      toast.success('Deal created');
      if (res?.id) {
        navigate(`/deals/${res.id}`);
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Failed to create deal')
  })

  // ── Notes Mutations ──
  const addNoteMut = useMutation({
    mutationFn: (content: string) => customerNotesApi.create(id!, content),
    onSuccess: () => { invalidate(); toast.success('Note added') }
  })
  const updateNoteMut = useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) => customerNotesApi.update(id!, noteId, content),
    onSuccess: () => { invalidate(); toast.success('Note updated') }
  })
  const deleteNoteMut = useMutation({
    mutationFn: (noteId: string) => customerNotesApi.delete(id!, noteId),
    onSuccess: () => { invalidate(); toast.success('Note deleted') }
  })

  if (loadingCustomer) return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><CircularProgress /></Box>
  if (!customer) return <Box sx={{ p: 4 }}><Typography>Not found</Typography></Box>

  const joinedDate = new Date(customer.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })

  // ── Compute deal stats ────────────────────────────────────────────────────────
  const allDeals = dealsData?.items ?? []
  const openDeals   = allDeals.filter(d => d.status === 'OPEN')
  const wonDeals    = allDeals.filter(d => d.status === 'WON')
  const closedDeals = allDeals.filter(d => d.status !== 'OPEN')  // WON + LOST

  const sumAmt = (ds: typeof allDeals) =>
    ds.reduce((s, d) => s + (d.amount ?? 0), 0)

  const fmt = (n: number) =>
    n === 0 ? '₹0'
    : n >= 10_00_000 ? `₹${(n / 10_00_000).toFixed(n % 10_00_000 === 0 ? 0 : 1)}L`
    : n >= 1_00_000  ? `₹${(n / 1_00_000).toFixed(n % 1_00_000  === 0 ? 0 : 1)}L`
    : n >= 1_000     ? `₹${(n / 1_000).toFixed(0)}K`
    : `₹${n}`

  const openPipeline   = sumAmt(openDeals)
  const closedPipeline = sumAmt(closedDeals)
  const totalWon       = sumAmt(wonDeals)

  // Last activity from activity log
  const lastLog = activityLogs[0]
  const lastActivityTime = lastLog
    ? (() => {
        const diff = Date.now() - new Date(lastLog.createdAt).getTime()
        const mins = Math.floor(diff / 60_000)
        if (mins < 60)  return `${mins}m ago`
        const hrs = Math.floor(diff / 3_600_000)
        if (hrs < 24)   return `${hrs}h ago`
        return `${Math.floor(diff / 86_400_000)}d ago`
      })()
    : '—'
  const ACTION_LABELS: Record<string, string> = { CREATE: 'Created', UPDATE: 'Updated', DELETE: 'Deleted' }
  const lastActivitySub = lastLog
    ? `${ACTION_LABELS[lastLog.action] ?? lastLog.action} · ${lastLog.entityName || lastLog.entityType}`
    : 'No activity yet'

  const customerTabs = [
    { label: 'Activity', content: <UniversalActivityTimeline logs={activityLogs} isLoading={loadingActivity} entityType="customer" /> },
    { label: 'Leads', content: <LeadsTab leads={leads} isLoading={loadingLeads} onNavigate={(lid) => navigate(`/leads/${lid}`)} /> },
    { label: 'Deals', content: <DealsTab customerId={id!} onAddDeal={() => setDealDrawerOpen(true)} onNavigate={(did) => navigate(`/deals/${did}`)} /> },
    { label: 'Contacts', content: <ContactsTab contacts={contacts} isLoading={loadingContacts} onNavigate={(cid) => navigate(`/contacts/${cid}`)} onAdd={() => setContactDrawerOpen(true)} customer={customer} /> },
    { 
      label: 'Comments', 
      content: (
        <Box>
          <CommentInput onAdd={async (c) => { await addNoteMut.mutateAsync(c) }} isAdding={addNoteMut.isPending} />
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : notes.length === 0 ? (
            <CommentsEmptyState />
          ) : (
            <Stack spacing={0}>
              {notes.map((note, index) => (
                <CommentItem 
                  key={note.id} 
                  note={note} 
                  onUpdate={async (c) => { await updateNoteMut.mutateAsync({ noteId: note.id, content: c }) }}
                  onDelete={() => deleteNoteMut.mutate(note.id)}
                  isUpdating={updateNoteMut.isPending}
                  isLast={index === notes.length - 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      )
    },
    { label: 'Attachments', content: <AttachmentsSection moduleType="customer" recordId={customer.id} canModify={true} /> },
  ]

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: '#F9FAFB' }}>
      <EntityDetailHeader
        title={customer.companyName || customer.name}
        breadcrumbs={[{ label: 'Companies', to: '/customers' }, { label: customer.companyName || customer.name }]}
        avatarInitials={getInitials(customer.companyName || customer.name)}
        badge={<Chip label="Active" size="small" sx={{ height: 20, fontSize: '11px', fontWeight: 700, bgcolor: '#16a34a', color: '#fff' }} icon={<Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#fff', ml: 1 }} />} />}
        subtitles={[
          { icon: <LocationIcon sx={{ fontSize: '14px' }} />, text: customer.city || 'Noida, India' },
          { icon: <DateIcon sx={{ fontSize: '14px' }} />, text: `Customer since ${joinedDate}` },
          ...(customer.websiteUrl ? [{
            icon: <LinkIcon sx={{ fontSize: '14px' }} />,
            text: (
              <Box
                component="a"
                href={customer.websiteUrl.startsWith('http') ? customer.websiteUrl : `https://${customer.websiteUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: themeColors.primary } }}
              >
                {customer.websiteUrl}
              </Box>
            )
          }] : []),
        ]}
        onBack={() => navigate('/customers')}
        stats={[
          {
            label:   'Closed Deals',
            value:   String(closedDeals.length),
            subtext: closedPipeline > 0 ? `${fmt(closedPipeline)} pipeline` : 'No closed deals',
          },
          {
            label:   'Open Deals',
            value:   String(openDeals.length),
            subtext: openPipeline > 0 ? `${fmt(openPipeline)} pipeline` : 'No open deals',
          },
          {
            label:   'Total won',
            value:   fmt(totalWon),
            subtext: wonDeals.length > 0 ? `${wonDeals.length} won deal${wonDeals.length !== 1 ? 's' : ''}` : 'No won deals',
          },
          {
            label:   'Last activity',
            value:   lastActivityTime,
            subtext: lastActivitySub,
          },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setLeadDrawerOpen(true)} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#374151', borderColor: '#E5E7EB', height: '36px' }}>New Lead</Button>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setDealDrawerOpen(true)} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#374151', borderColor: '#E5E7EB', height: '36px' }}>New Deal</Button>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setContactDrawerOpen(true)} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#374151', borderColor: '#E5E7EB', height: '36px' }}>New Contact</Button>
            <Button variant="outlined" size="small" startIcon={<CommentIcon />} onClick={() => setActiveTab(4)} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#374151', borderColor: '#E5E7EB', height: '36px' }}>Comment</Button>
          </Stack>
        }
      />

      <EntityDetailLayout
        sidebar={
          <>
            <SideCard title="Basic Info">
              <Stack spacing={0.5}>
                <InfoRow icon={<PersonIcon sx={{ fontSize: 16 }} />} label="Primary Contact" value={customer.name || 'None'} />
                {customer.designation && (
                  <InfoRow icon={<WorkIcon sx={{ fontSize: 16 }} />} label="Designation" value={customer.designation} />
                )}
                <InfoRow icon={<EmailIcon />} label="Primary Email" value={customer.email || 'No email'} />
                <InfoRow icon={<CallIcon />} label="Primary Phone" value={maskPhoneNumber(customer.phone)} />
              </Stack>
            </SideCard>
            <DescriptionSection
              value={customer.description}
              onSave={(val) => updateMut.mutate({ description: val })}
              isPending={updateMut.isPending}
              canEdit={canEdit}
            />

            <AddressAndTaxInfoCard
              address={customer.address}
              gst={customer.gst}
              pincode={customer.pincode}
              canEdit={canEdit}
              isSaving={updateMut.isPending}
              onSave={(data) => updateMut.mutate(data)}
            />

            <SideCard title="Business Info">
              <Stack spacing={0.5}>
                <InfoRow icon={<IndustryIcon />} label="Business" value={customer.orgName || '—'} />
                <InfoRow icon={<IndustryIcon />} label="LOB"      value={customer.lobName || '—'} />
                <InfoRow icon={<FlagIcon sx={{ fontSize: 16 }} />} label="Source" value={customer.source || '—'} />
                {customer.industryType && (
                  <InfoRow icon={<IndustryIcon sx={{ fontSize: 16 }} />} label="Industry" value={customer.industryType} />
                )}
              </Stack>
            </SideCard>
          </>
        }
      >
        <EntityDetailTabs tabs={customerTabs} activeTab={activeTab} onChange={setActiveTab} />
      </EntityDetailLayout>

      <LeadDrawer
        open={leadDrawerOpen}
        onClose={() => setLeadDrawerOpen(false)}
        onSubmit={(data) => createLeadMut.mutate(data)}
        isSubmitting={createLeadMut.isPending}
        initialData={{ 
          companyName: customer.companyName || customer.name,
          gst: customer.gst || '',
          address: customer.address || '',
          pincode: customer.pincode || '',
          companyWebsite: customer.websiteUrl || '',
          industry: customer.industryType || '',
          orgId: customer.orgId ?? '',
          lobId: customer.lobId ?? '',
          warehouseId: customer.warehouseId ?? ''
        }}
      />

      <DealDrawer
        open={dealDrawerOpen}
        onClose={() => setDealDrawerOpen(false)}
        onSubmit={(data) => createDealMut.mutate({
          title: data.dealName,
          customerId: data.customerId || customer.id,
          contactId: data.contactId || undefined,
          stage: data.stage || undefined,
          amount: data.amount ? parseFloat(data.amount) : undefined,
          currency: data.currency || 'INR',
          probabilityPct: data.probabilityPct ? parseInt(data.probabilityPct) : undefined,
          priority: data.priority || undefined,
          source: data.source || undefined,
          customerTypeId: data.customerTypeId ? parseInt(data.customerTypeId) : undefined,
          description: data.description || undefined,
          expectedCloseDate: data.expectedClosingDate || undefined,
          orgId: data.orgId || customer.orgId || undefined,
          lobId: data.lobId || customer.lobId || undefined,
        })}
        isSubmitting={createDealMut.isPending}
        initialData={{ customerId: customer.id }}
        initialCustomer={{ id: customer.id, name: customer.companyName || customer.name }}
      />

      <ContactDrawer
        open={contactDrawerOpen}
        onClose={() => setContactDrawerOpen(false)}
        onCreated={() => { invalidate(); setContactDrawerOpen(false) }}
        isSuperAdmin={authStore.user?.roleName === 'SUPER_ADMIN'}
        initialCustomer={{
          id: customer.id,
          label: customer.companyName ?? customer.name,
          orgId: customer.orgId
        }}
      />

      <TagModal
        open={tagModalOpen}
        onClose={() => setTagModalOpen(false)}
        allTags={allTags}
        attachedTagIds={new Set((customer.tags || []).map((t) => t.id))}
        onAdd={(tid) => addTagMut.mutate(tid)}
        isAdding={addTagMut.isPending}
      />
    </Box>
  )
})

export default CustomerDetailPage
